import { itemsCol, layerOf, layerEntriesOf, layerListEntryUuid } from '../../shared/array-types';
import { fromBaseRecord } from '../compile/from-base-context';

type Row = Record<string, unknown>;
type Field = { storage?: string; serializer?: string };
type Rule = { id: string; op: string; fields: string[]; severity?: string; message?: string; item_types?: string[]; completeness?: boolean };
export interface PublishData {
    catalog: Record<string, Field>;
    rules: { mode: string; rules: Rule[] };
    descent: Record<string, Record<string, { path: string; note?: string | null }>>;
    objectTypes: string[];
    aoeTypes: Row[];
    selfRangeId: string;
    bodyEssenceId: string;
}
export type PublishFindings = { errors: string[]; warnings: string[] };
export type AuthoringFinding = { message: string; field?: string };
export type AuthoringFindings = { errors: AuthoringFinding[]; warnings: AuthoringFinding[] };
export type AuthoringPolicy = 'publish' | 'final' | 'draft' | 'audit';
const scalar = (value: unknown): string => typeof value === 'string' || typeof value === 'number' ? String(value).trim()
    : typeof value === 'boolean' ? (value ? '1' : '') : '';
const value = (data: Row, key: string): string => Array.isArray(data[key])
    ? (data[key] as unknown[]).map(scalar).find(Boolean) ?? '' : scalar(data[key]);

/** Retain direct values for authored/dead fields; resolved compiled values take precedence. */
export function publishAuthoringFields(direct: Row, compiled: Row, facts: PublishData): Row {
    const data: Row = { item_type: itemsCol(direct, 'item_type') ?? '' };
    const overlay = new Set(['range', 'flavor_text', 'v_dice', 'size', 'body', 'mind', 'soul', 'cr', 'creature_type', 'weight',
        'item_composition', 'weapon_classification', 'ability_activation_charges', 'skill_roll_mod', 'skill_roll_mod_number',
        'skill_rolls_mod', 'aoe_type', 'aoe_distance', 'aoe_distance_depth', 'aoe_distance_height', 'weapon_essence',
        ...facts.rules.rules.flatMap(rule => rule.fields)]);
    const fields = new Set([...overlay, 'arcing_trajectory', ...Object.keys(facts.descent)]);
    for (const [field, meta] of Object.entries(facts.catalog)) {
        if (!fields.has(field)) continue;
        const multi = meta.serializer === 'multi' || meta.serializer === 'structured_rows';
        const read = (row: Row): unknown => meta.storage === 'items_column' ? itemsCol(row, field)
            : multi ? layerEntriesOf(row, field).map(entry => fromBaseRecord(entry) ? layerListEntryUuid(entry) : entry)
                .filter(entry => entry !== null && entry !== undefined && entry !== '') : layerOf(row, field);
        const authored = read(direct);
        if (authored !== undefined) data[field] = authored;
        if (!overlay.has(field)) continue;
        const resolved = read(compiled);
        if (resolved !== null && resolved !== undefined && resolved !== '' && !(Array.isArray(resolved) && !resolved.length))
            data[field] = resolved;
    }
    const compiledType = itemsCol(compiled, 'item_type');
    if (compiledType?.trim()) data.item_type = compiledType;
    return data;
}

/** Shared hand checks: draft relaxes completeness only; Publish reports them as warnings. */
function handFindings(data: Row, facts: PublishData, complete: boolean): AuthoringFinding[] {
    const findings: AuthoringFinding[] = [], type = value(data, 'item_type'), val = (key: string) => value(data, key);
    const add = (field: string, message: string) => findings.push({ field, message });
    const require = (key: string, message: string) => { if (val(key) === '') add(key, message); };
    if (complete) {
        if (['full_ability', 'ability', 'quick_ability', 'free_ability', 'reaction_ability', 'spell'].includes(type)) {
            require('range', type === 'spell' ? 'This spell needs a range!' : 'This ability type needs a range!');
            require('flavor_text', type === 'spell' ? 'All spells need flavor text!' : 'All ability types need flavor text!');
        } else if (type === 'creature') {
            for (const [key, label] of Object.entries({ v_dice: 'v_dice', size: 'size', body: 'Body Essence', mind: 'Mind Essence',
                soul: 'Soul Essence', cr: 'CR', creature_type: 'creature type', weight: 'weight' }))
                require(key, `Creatures must have a ${label}!`);
            require('flavor_text', 'Creatures must have flavor text!');
        } else if (!['weapon', 'natural_weapon'].includes(type) && facts.objectTypes.includes(type)) {
            require('flavor_text', 'All objects need flavor text!');
            require('weight', 'All objects need weight!');
            require('item_composition', 'All objects need an item composition!');
        }
    }
    if (['weapon', 'natural_weapon'].includes(type)) require('weapon_classification', 'Weapons must have a weapon classification.');
    if (['attack_ability', 'opportunity_attack_ability', 'passive_ability'].includes(type)) {
        if (val('range')) add('range', 'Passive, OAA, and Attack abilities do not use a range and use the range of the weapon if needed.');
        if (val('ability_activation_charges')) add('ability_activation_charges', 'Passive, OAA, and Attack abilities do not use ability_activation_charges and are considered "at will" and "always on".');
    }
    if (val('skill_roll_mod') && !val('skill_roll_mod_number')) add('skill_roll_mod_number', 'Skill roll modifier number cannot be blank while skill roll modifier value is NOT blank!');
    for (const [key, label] of Object.entries({ aoe_distance: 'AOE Distance', aoe_distance_depth: 'AOE Distance Depth', aoe_distance_height: 'AOE Distance Height' }))
        if (val(key) && !/^[0-9x ]+$/.test(val(key))) add(key, `${label} can only contain numbers, spaces, and "x"`);
    const aoe = val('aoe_type'), aoeRow = facts.aoeTypes.find(row => String(itemsCol(row, 'item_id')).toUpperCase() === aoe.toUpperCase());
    const name = String(itemsCol(aoeRow, 'variable_name') ?? '').toLowerCase();
    const arcing = ['1', 'on', 'true'].includes(val('arcing_trajectory').toLowerCase());
    if (aoe) {
        if (name === 'circle' && val('aoe_distance_height') && (parseInt(val('aoe_distance_height'), 10) || 0) < 1)
            add('aoe_distance_height', 'AOE Type: Circle must have an AOE Distance Height of at least 1 ft.');
        if (val('aoe_distance_depth') && ['circle', 'cone', 'cube', 'cylinder', 'sphere'].includes(name)) add('aoe_distance_depth', 'This AOE Type does not use AOE Distance Depth!');
        if (['line', 'square'].includes(name) && ['aoe_distance', 'aoe_distance_depth', 'aoe_distance_height'].some(key => !val(key)))
            add(['aoe_distance', 'aoe_distance_depth', 'aoe_distance_height'].find(key => !val(key)) ?? 'aoe_distance', 'This AOE Type requires all three dimensions for its AOE Distances!');
        if (arcing && ['line', 'cone'].includes(name)) add('arcing_trajectory', 'Arcing Trajectory cannot be combined with a Line or Cone AOE — an arc lobs a centered shape.');
    } else if (arcing) add('arcing_trajectory', 'Arcing Trajectory requires an AOE Type — it is the flight path of an area effect.');
    if (arcing && val('range').toUpperCase() === facts.selfRangeId.toUpperCase()) add('arcing_trajectory', 'Arcing Trajectory cannot be combined with a Self range — a Self area never leaves the caster.');
    if (val('weapon_essence') && val('weapon_essence').toUpperCase() === facts.bodyEssenceId.toUpperCase())
        add('weapon_essence', 'Weapon essences cannot be body, this is the default essence for weapon attacks anyway!');
    return findings;
}

/** Evaluate once with the caller's final, draft, Publish or unsafe-table audit policy. */
export function validateItemAuthoring(data: Row, facts: PublishData, policy: AuthoringPolicy): AuthoringFindings {
    const result: AuthoringFindings = { errors: [], warnings: [] };
    const draft = policy === 'draft';
    if (policy !== 'audit') result[policy === 'publish' ? 'warnings' : 'errors'] = handFindings(data, facts, !draft);
    const present = (key: string): boolean => {
        const raw = data[key];
        if (raw === undefined || raw === null || raw === '') return false;
        if (facts.catalog[key]?.serializer === 'checkbox') return [1, '1', true, 'on'].includes(Array.isArray(raw) ? raw[0] : raw);
        return !Array.isArray(raw) || raw.some(entry => fromBaseRecord(entry) ? scalar(itemsCol(entry, 'item_id')) !== '' : scalar(entry) !== '');
    };
    const refs = (key: string): string[] => (Array.isArray(data[key]) ? data[key] as unknown[] : [data[key]])
        .map(entry => scalar(fromBaseRecord(entry) ? itemsCol(entry, 'item_id') : entry).toUpperCase()).filter(Boolean);
    const num = (key: string): number | null => {
        const raw = Array.isArray(data[key]) ? data[key][0] : data[key];
        return raw !== undefined && raw !== null && raw !== '' && Number.isFinite(Number(raw)) ? Number(raw) : null;
    };
    const emit = (rule: Rule, message: string, error = rule.severity === 'error' && !(draft && rule.completeness), field = rule.fields[0]) =>
        (policy !== 'audit' && facts.rules.mode === 'enforce' && error ? result.errors : result.warnings).push({ message, field });
    const type = value(data, 'item_type');
    for (const rule of facts.rules.rules) {
        const fields = rule.fields, a = fields[0] ?? '', b = fields[1] ?? '';
        let hit = false;
        switch (rule.op) {
            case 'present_requires': hit = present(a) && fields.slice(1).some(key => !present(key)); break;
            case 'both_or_neither': hit = present(a) !== present(b); break;
            case 'not_both': hit = present(a) && present(b); break;
            case 'disjoint_refs': { const left = new Set(refs(a)); hit = refs(b).some(ref => left.has(ref)); break; }
            case 'numeric_lte': { const left = num(a), right = num(b); hit = left !== null && right !== null && left > right; break; }
            case 'numeric_nonneg': hit = fields.some(key => { const number = num(key); return number !== null && number < 0; }); break;
            case 'dead_field': hit = fields.some(present); break;
            case 'item_type_requires': hit = !!rule.item_types?.includes(type) && fields.some(key => !present(key)); break;
            case 'duration_shape': { const number = num(a), duration = value(data, b).toLowerCase();
                hit = duration === 'permanent' ? number !== null && number !== 0 : duration !== '' ? number === null || number <= 0 : number !== null && number !== 0;
                break;
            }
            case 'descent_contract':
                for (const [payload, cells] of Object.entries(facts.descent)) {
                    if (!present(payload)) continue;
                    const cell = cells[type] ?? { path: 'undeclared' }, prefix = `[${rule.id}] ${type}.${payload}: `;
                    if (cell.path === 'unsupported') emit(rule, prefix + 'no engine reader descends this container for this payload — the nested grant will be SILENTLY DEAD. ' + (rule.message ?? ''), rule.severity === 'error' && !draft, payload);
                    else if (cell.path === 'undeclared') emit(rule, prefix + 'this (container, payload) cell is NOT in the descent contract — verify a reader actually walks it, then declare it in ContainerDescentContract.', false, payload);
                    else if (cell.path === 'partial') emit(rule, prefix + 'only PARTIALLY reachable — ' + (cell.note ?? 'see ContainerDescentContract') + '. Verify the authored shape matches before relying on it.', false, payload);
                }
                break;
        }
        if (hit) emit(rule, `[${rule.id}] ${rule.message ?? 'rule violated'}`);
    }
    return result;
}

export function publishHandFindings(data: Row, facts: PublishData): string[] {
    return handFindings(data, facts, true).map(finding => finding.message);
}

export function validatePrivateItemPublish(data: Row, facts: PublishData): PublishFindings {
    const result = validateItemAuthoring(data, facts, 'publish');
    return { errors: result.errors.map(finding => finding.message), warnings: result.warnings.map(finding => finding.message) };
}
